import styled            from 'styled-components';

import { cssFlexRow,
         themeColors, 
         themeTypos,
         Typo,
         typoAsCSS     } from 'src/providers/theme/shared';



type ButtonLookAndFeel = {
  typo: Typo;

  activeBgColor: string;
  disableBgColor: string;
  activeTextColor: string;
  disableTextColor: string;
  activeBorderColor: string;
  disableBorderColor: string;

  padding: number[];

  height: number | null;
  fillWidth: boolean;
  borderSize: number;
  borderRadius?: number;
  border?: string;
};

const baseButtonLookAndFeel = {
  borderSize: 1,
  fillWidth: true,
  padding: [0, 0],
};

const baseBlueFillButtonLookAndFeel = {
  ...baseButtonLookAndFeel,
  activeBgColor: themeColors.AccentBlue2,
  disableBgColor: themeColors.SecondaryGray3,
  activeTextColor: themeColors.PrimaryWhite,
  disableTextColor: themeColors.PrimaryWhite,
  activeBorderColor: themeColors.AccentBlue2,
  disableBorderColor: themeColors.SecondaryGray3,
};

const baseBlueOutlineButtonLookAndFeel = {
  ...baseButtonLookAndFeel,
  activeBgColor: themeColors.PrimaryWhite,
  disableBgColor: themeColors.PrimaryWhite,
  activeTextColor: themeColors.AccentBlue2,
  disableTextColor: themeColors.SecondaryGray3,
  activeBorderColor: themeColors.AccentBlue2,
  disableBorderColor: themeColors.SecondaryGray3,
}

const baseLightBlueOutlineButtonLookAndFeel = {
  ...baseButtonLookAndFeel,
  activeBgColor: themeColors.PrimaryWhite,
  disableBgColor: themeColors.PrimaryWhite,
  activeTextColor: themeColors.AccentBlue2,
  disableTextColor: themeColors.SecondaryGray3,
  activeBorderColor: themeColors.LightBlue1,
  disableBorderColor: themeColors.SecondaryGray3,
}

const baseRedFillButtonLookAndFeel = {
  ...baseButtonLookAndFeel,
  activeBgColor: themeColors.AccentRed1,
  disableBgColor: themeColors.SecondaryGray3,
  activeTextColor: themeColors.PrimaryWhite,
  disableTextColor: themeColors.PrimaryWhite,
  activeBorderColor: themeColors.AccentRed1,
  disableBorderColor: themeColors.SecondaryGray3,
}

const baseRedOutlineButtonLookAndFeel = {
  ...baseButtonLookAndFeel,
  activeBgColor: themeColors.PrimaryWhite,
  disableBgColor: themeColors.PrimaryWhite,
  activeTextColor: themeColors.AccentRed1,
  disableTextColor: themeColors.SecondaryGray3,
  activeBorderColor: themeColors.AccentRed1,
  disableBorderColor: themeColors.SecondaryGray3,
}

const baseEmptyButtonLookAndFeel = {
  activeBgColor: 'transparent',
  disableBgColor: themeColors.PrimaryWhite,
  activeTextColor: '#CCD9EC',
  disableTextColor: themeColors.SecondaryGray3,
  activeBorderColor: 'none',
  disableBorderColor: 'none',
  borderRadius: 'none',
  borderSize: 0,
  border: 'none',
  fillWidth: true
}

const h48 = {
  typo: themeTypos.GRTSKREG_W400_S13_LH20,
  height: 48,
}

const h36 = {
  typo: themeTypos.GRTSKREG_W400_S11_LH18,
  height: 36,
}

const h28 = {
  typo: themeTypos.GRTSKREG_W400_S11_LH18,
  height: 28,
}

const button_h48bluefill: ButtonLookAndFeel = {
  ...baseBlueFillButtonLookAndFeel,
  ...h48,
};

const button_h36bluefill: ButtonLookAndFeel = {
  ...baseBlueFillButtonLookAndFeel,
  ...h36,
}

const button_h48blueoutline: ButtonLookAndFeel = {
  ...baseBlueOutlineButtonLookAndFeel,
  ...h48,
};

const button_h36blueoutline: ButtonLookAndFeel = {
  ...baseBlueOutlineButtonLookAndFeel,
  ...h36,
};

const button_h36lightblueoutline: ButtonLookAndFeel = {
  ...baseLightBlueOutlineButtonLookAndFeel,
  ...h36,
  borderRadius: 10,
};

const button_h48bluefillround: ButtonLookAndFeel = {
  ...baseBlueFillButtonLookAndFeel,
  ...h48,
  borderRadius: 1000,
};

const button_h48lightblueoutline: ButtonLookAndFeel = {
  ...baseLightBlueOutlineButtonLookAndFeel,
  ...h48,
  borderRadius: 12,
};

const button_h28redfill: ButtonLookAndFeel = {
  ...baseRedFillButtonLookAndFeel,
  ...h28,
};

const button_h36redoutline: ButtonLookAndFeel = {
  ...baseRedOutlineButtonLookAndFeel,
  ...h36,
};

const button_h48empty: ButtonLookAndFeel = {
  ...baseEmptyButtonLookAndFeel,
  ...h48,
  padding: [0, 0],
  borderRadius: 1000,
};

const button_primaryBig: ButtonLookAndFeel = {
  ...baseBlueFillButtonLookAndFeel,
  padding: [12, 20],
  activeBgColor: themeColors.AccentPrimaryBluePrimary01,
  typo: themeTypos.GRTSKREG_W500_S14_LH18,
  height: null,
  fillWidth: false,
  borderRadius: 12,
}

const button_primary: ButtonLookAndFeel = {
  ...button_primaryBig,
  padding: [9, 20],
  height: null,
  typo: themeTypos.GRTSKREG_W400_S12_LH16,
}

const button_secondaryBig: ButtonLookAndFeel = {
  ...baseBlueOutlineButtonLookAndFeel,
  padding: [12, 20],
  typo: themeTypos.GRTSKREG_W500_S14_LH18,
  activeBgColor: themeColors.NeutralBlue01,
  activeBorderColor: "transparent",
  activeTextColor: themeColors.TextAccentBlueCobolt100,
  height: null,
  fillWidth: false,
  borderRadius: 12,
}

const button_secondary: ButtonLookAndFeel = {
  ...button_secondaryBig,
  padding: [9, 20],
  height: null,
  typo: themeTypos.GRTSKREG_W400_S12_LH16,
}

const button_outlinedBig: ButtonLookAndFeel = {
  ...baseBlueOutlineButtonLookAndFeel,
  padding: [12, 20],
  typo: themeTypos.GRTSKREG_W500_S14_LH18,
  activeBorderColor: themeColors.AccentPrimaryBluePrimary01,
  activeTextColor: themeColors.TextAccentBlueCobolt100,
  height: null,
  fillWidth: false,
  borderRadius: 12,
}

const button_outlined: ButtonLookAndFeel = {
  ...button_outlinedBig,
  padding: [9, 20],
  height: null,
  typo: themeTypos.GRTSKREG_W400_S12_LH16,
}

const button_textBig: ButtonLookAndFeel = {
  ...baseEmptyButtonLookAndFeel,
  padding: [12, 20],
  activeTextColor: themeColors.TextAccentBlueCobolt100,
  borderRadius: 0,
  typo: themeTypos.GRTSKREG_W500_S14_LH18,
  height: null,
  fillWidth: false,
}

const button_text: ButtonLookAndFeel = {
  ...button_textBig,
  padding: [9, 20],
  height: null,
  typo: themeTypos.GRTSKREG_W400_S12_LH16,
}

const button_cancel: ButtonLookAndFeel = {
  ...baseEmptyButtonLookAndFeel,
  padding: [9, 20],
  activeBgColor: themeColors.AccentPrimaryRedSecondary,
  activeTextColor: themeColors.TextPermanentWhite,
  borderRadius: 999,
  typo: themeTypos.GRTSKREG_W400_S12_LH16,
  height: null,
  fillWidth: false,
}

export type ButtonVariants = 'h48bluefill' 
                           | 'h36bluefill' 
                           | 'h48blueoutline' 
                           | 'h36blueoutline' 
                           | 'h48lightblueoutline' 
                           | 'h48bluefillrounnd' 
                           | 'h36lightblueoutline' 
                           | 'h28redfill' 
                           | 'h36redoutline' 
                           | 'h48empty'
                           | 'primaryBig'
                           | 'primary'
                           | 'secondaryBig'
                           | 'secondary'
                           | 'outlinedBig'
                           | 'outlined'
                           | 'textBig'
                           | 'text'
                           | 'cancel'

export const button_variants: Map<ButtonVariants, ButtonLookAndFeel> = new Map<ButtonVariants, ButtonLookAndFeel>([
  ['h48bluefill', button_h48bluefill],
  ['h36bluefill', button_h36bluefill],
  ['h48blueoutline', button_h48blueoutline],
  ['h36blueoutline', button_h36blueoutline],
  ['h48lightblueoutline', button_h48lightblueoutline],
  ['h36lightblueoutline', button_h36lightblueoutline],
  ['h48bluefillrounnd', button_h48bluefillround],
  ['h28redfill', button_h28redfill],
  ['h36redoutline', button_h36redoutline],
  ['h48empty', button_h48empty],
  ['primaryBig',    button_primaryBig],
  ['primary',       button_primary],
  ['secondaryBig',  button_secondaryBig],
  ['secondary',     button_secondary],
  ['outlinedBig',   button_outlinedBig],
  ['outlined',      button_outlined],
  ['textBig',       button_textBig],
  ['text',          button_text],
  ['cancel',        button_cancel],
]);

export const Container = styled.button<{
  lookAndFeel: ButtonLookAndFeel;
  isIcon: boolean;
}>`
  ${ (args) => {
    if (args.isIcon) {
      return `width: ${args.lookAndFeel.height}px;`
    } else if (args.lookAndFeel.fillWidth) {
      return `width: 100%;`
    } else {
      return '';
    }
  } }
  // height: ${(args) => args.lookAndFeel.height}px;
  ${(args) => args.lookAndFeel.height !== null ? 
    `height: ${args.lookAndFeel.height}px;` :
    '' 
  }

  ${cssFlexRow};
  justify-content: center;
  align-items: center;
  cursor: pointer;
  padding: ${(args) => args.lookAndFeel.padding[0]}px ${(args) => args.lookAndFeel.padding[1]}px;

  background-color: ${(args) => args.lookAndFeel.activeBgColor};
  border: ${(args) => args.lookAndFeel.border ? `${args.lookAndFeel.border}` : `${args.lookAndFeel.borderSize}px solid ${args.lookAndFeel.activeBorderColor}`};

  border-radius: ${(args) => args.lookAndFeel.borderRadius ?? 0}px;
  &:disabled {
    background-color: ${(args) => args.lookAndFeel.disableBgColor};
    border: ${(args) => `${args.lookAndFeel.borderSize}px solid ${args.lookAndFeel.disableBorderColor}`};
  }

  color: ${(args) => args.lookAndFeel.activeTextColor};
  &:disabled span {
    color: ${(args) => args.lookAndFeel.disableTextColor};
  }
`;

export const Label = styled.span<{
  lookAndFeel: ButtonLookAndFeel,
}>`
  ${(args) => typoAsCSS(args.lookAndFeel.typo)}
  color: ${(args) => args.lookAndFeel.activeTextColor};
`;
